import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Globe, Building2, MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "../../lib/motion";
import { BRAND } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", country: "", email: "", phone: "", product_interest: "", quantity: "", message: "" };

export default function Contact({ prefill }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, product_interest: prefill }));
  }, [prefill]);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! Your inquiry has been received. We'll respond shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-lg border border-verra-blue/15 bg-white px-4 py-3 text-sm text-verra-blue outline-none focus:border-verra-green transition-colors";

  return (
    <section id="contact" data-testid="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <span className="overline">Get in touch</span>
            <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-verra-blue">
              Request a quotation for your next order.
            </h2>
            <p className="mt-6 text-lg text-verra-blue/70 max-w-md leading-relaxed">
              Tell us what you need — from single pallets to full container shipments. Our export team responds fast.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-verra-grey flex items-center justify-center"><Mail className="w-5 h-5 text-verra-green" /></span>
              <span className="text-verra-blue group-hover:text-verra-green transition-colors">{BRAND.email}</span>
            </a>
            <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-verra-grey flex items-center justify-center"><Phone className="w-5 h-5 text-verra-green" /></span>
              <span className="text-verra-blue group-hover:text-verra-green transition-colors">{BRAND.phone}</span>
            </a>
            <a href={`https://${BRAND.website}`} target="_blank" rel="noopener noreferrer" data-testid="contact-website" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-verra-grey flex items-center justify-center"><Globe className="w-5 h-5 text-verra-green" /></span>
              <span className="text-verra-blue group-hover:text-verra-green transition-colors">{BRAND.website}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-verra-grey flex items-center justify-center"><Building2 className="w-5 h-5 text-verra-green" /></span>
              <span className="text-verra-blue/70">{BRAND.parent} · We export with trust</span>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-11 h-11 rounded-full bg-verra-grey flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-verra-green" /></span>
              <span className="text-verra-blue/70 text-sm leading-relaxed" data-testid="contact-address">{BRAND.address}</span>
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <form data-testid="inquiry-form" onSubmit={submit} className="rounded-3xl bg-verra-base border border-verra-blue/10 p-7 md:p-9">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="input-name" name="name" value={form.name} onChange={change} placeholder="Name *" className={field} />
              <input data-testid="input-company" name="company" value={form.company} onChange={change} placeholder="Company" className={field} />
              <input data-testid="input-country" name="country" value={form.country} onChange={change} placeholder="Country" className={field} />
              <input data-testid="input-email" name="email" type="email" value={form.email} onChange={change} placeholder="Email *" className={field} />
              <input data-testid="input-phone" name="phone" value={form.phone} onChange={change} placeholder="Phone" className={field} />
              <input data-testid="input-product" name="product_interest" value={form.product_interest} onChange={change} placeholder="Product Interest" className={field} />
              <input data-testid="input-quantity" name="quantity" value={form.quantity} onChange={change} placeholder="Quantity" className={`${field} sm:col-span-2`} />
              <textarea data-testid="input-message" name="message" value={form.message} onChange={change} placeholder="Message" rows={4} className={`${field} sm:col-span-2 resize-none`} />
            </div>
            <button
              data-testid="inquiry-submit-btn"
              type="submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-verra-blue text-white font-semibold py-4 hover:bg-verra-green transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending…" : "Submit Inquiry"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
